import React, { useState, useEffect, useMemo } from "react";
import { useSelector } from 'react-redux';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell
} from 'recharts';
import {
  Briefcase,
  ArrowUpRight,
  Globe,
  Zap,
  Calendar,
  Filter,
  Users,
  User as UserIcon,
  ChevronDown,
  Target,
  TrendingUp
} from 'lucide-react';
import { fetchzcql } from '../api/reusableApis';
import {
  transformWeeklyData,
  transformMonthlyData,
  transformCustomRangeData
} from '.././resuablefunc';
import { getalluser_ } from '../api/reusableApis'
import RevenueInsights from "./RevenueInsights";


const COLORS = ["#10B981", "#3B82F6", "#F59E0B", "#8B5CF6", "#EC4899"];


const CHART_MAPPING = {
  "today": "weekly",
  "yesterday": "weekly",
  "this week": "weekly",
  "this month": "monthly",
  "custom": "yearly"
};


const formatDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};


const getDateRange = (filter) => {
  const now = new Date();

  let startDate;
  let endDate;

  switch (filter) {
    case "today":
      startDate = new Date();
      startDate.setHours(0, 0, 0, 0);
      endDate = new Date(startDate);
      break;

    case "yesterday":
      startDate = new Date();
      startDate.setDate(startDate.getDate() - 1);
      startDate.setHours(0, 0, 0, 0);

      endDate = new Date(startDate);
      endDate.setHours(23, 59, 59, 999);
      break;

    case "this week":
      startDate = new Date();
      startDate.setDate(now.getDate() - now.getDay());
      startDate.setHours(0, 0, 0, 0);

      endDate = new Date(startDate);
      endDate.setDate(startDate.getDate() + 6);
      endDate.setHours(23, 59, 59, 999);
      break;

    case "this month":
      startDate = new Date(now.getFullYear(), now.getMonth(), 1);
      startDate.setHours(0, 0, 0, 0);

      endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      endDate.setHours(23, 59, 59, 999);
      break;

    default:
      return null;
  }

  return {
    fromDate: formatDate(startDate),
    toDate: formatDate(endDate)
  };
};
const getWeekRangeForDate = (date) => {
  const d = new Date(date);

  const startDate = new Date(d);
  startDate.setDate(d.getDate() - d.getDay());
  startDate.setHours(0, 0, 0, 0);

  const endDate = new Date(startDate);
  endDate.setDate(startDate.getDate() + 6);
  endDate.setHours(23, 59, 59, 999);

  return {
    fromDate: formatDate(startDate),
    toDate: formatDate(endDate)
  };
};





export default function MobileResponsiveDashboard() {
  const [globalTimeframe, setGlobalTimeframe] = useState("today");
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [teamFilter, setTeamFilter] = useState("today");
  const [metrics, setMetrics] = useState({});
  const [customDateRange, setcustomDateRange] = useState({ start: '', end: '' });
  const [allusers, setallusers] = useState([]);
  const [chartData, setChartData] = useState(
    {
      weekly: [
        { name: "Mon", tasks: 40 }, { name: "Tue", tasks: 35 }, { name: "Wed", tasks: 55 },
        { name: "Thu", tasks: 45 }, { name: "Fri", tasks: 70 }, { name: "Sat", tasks: 20 }, { name: "Sun", tasks: 10 },
      ],
      monthly: [
        { name: "Week 1", tasks: 120 }, { name: "Week 2", tasks: 150 }, { name: "Week 3", tasks: 180 }, { name: "Week 4", tasks: 140 },
      ],
      yearly: [
        { name: "Jan", tasks: 400 }, { name: "Feb", tasks: 300 }, { name: "Mar", tasks: 500 },
        { name: "Apr", tasks: 450 }, { name: "May", tasks: 600 }, { name: "Jun", tasks: 550 },
      ]
    }
  );


  const currentLoggedInUser = useSelector((state) => state.auth.currentUser);

  const fetchgraph = async (fromDate, toDate, filter) => {

    var daterange = null;
    if (filter === 'this month') {
      daterange = getDateRange('this month');
    } else if (filter === 'this week' || filter === 'today') {
      daterange = getDateRange('this week');
    } else if (filter === 'yesterday') {
      const yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);
      daterange = getWeekRangeForDate(yesterday);
    } else {
      daterange = { toDate: customDateRange.end, fromDate: customDateRange.start };
    }

    var bookingSql = `SELECT created_date, COUNT(ROWID) AS total FROM Bookings WHERE created_date BETWEEN '${daterange.fromDate}' AND '${daterange.toDate}' AND (status = 'BOOKED' or status = 'COMPLETED') GROUP BY created_date ORDER BY created_date`;

    const [bookRes] = await Promise.all([
      fetchzcql(bookingSql)
    ]);

    const safeTransform = (res, t) => {
      if (!res?.data?.success || !res?.data?.response) return [];
      const data = res.data.response;

      if (t === 'this month') return transformMonthlyData(data, new Date().getFullYear(), new Date().getMonth() + 1);
      if (t === 'custom') return transformCustomRangeData(data, daterange.fromDate, daterange.toDate);
      return transformWeeklyData(data);
    };

    const activeChartKeys = CHART_MAPPING[globalTimeframe] || "weekly";
    setChartData(prev => ({
      ...prev,
      [activeChartKeys]: safeTransform(bookRes, filter)
    }));




  };



  const fetchmetricsdata = async (filter) => {
    const daterange = getDateRange(filter);

    var fromDate = filter === 'custom' ? customDateRange.start : daterange?.fromDate;
    var toDate = filter === 'custom' ? customDateRange.end : daterange?.toDate;

    const queries = {
      tBooked: `SELECT COUNT(ROWID) FROM Bookings WHERE created_date BETWEEN '${fromDate}' AND '${toDate}' AND (status = 'BOOKED' or status = 'COMPLETED')`,
      totAtt: `SELECT COUNT(ROWID) FROM Bookings WHERE created_date BETWEEN '${fromDate}' AND '${toDate}'`,
      tRev: `SELECT SUM(total_amount) FROM Bookings WHERE created_date BETWEEN '${fromDate}' AND '${toDate}' AND (status = 'BOOKED' or  status = 'COMPLETED')`,
    };

    const results = await Promise.all(Object.values(queries).map(q => fetchzcql(q)));
    const getVal = (res, key) => Number(res?.data?.response?.[0]?.bookings?.[key]) || 0;

    const d = {
      tBooked: getVal(results[0], 'COUNT(ROWID)'),
      totAtt: getVal(results[1], 'COUNT(ROWID)'),
      tRev: getVal(results[2], 'SUM(total_amount)'),
    };


    fetchgraph(fromDate, toDate, filter);
    setMetrics({
      total_booking: d.tBooked,
      overall_conversion_rate: d.totAtt === 0 ? 0 : ((d.tBooked / d.totAtt) * 100).toFixed(1),
      today_revenue: d.tRev,
    })
  };

  const fetchusermetrics = async () => {
    if (!selectedUserId || !activeUser) return;

    const daterange = getDateRange(teamFilter);

    // 1. Construct the Queries
    const queries = {
      tBooked: `SELECT COUNT(ROWID) FROM Bookings WHERE created_date BETWEEN '${daterange.fromDate}' AND '${daterange.toDate}' AND (status = 'BOOKED' OR status = 'COMPLETED') AND telecaller_id = '${selectedUserId}'`,
      tRev: `SELECT SUM(total_amount) FROM Bookings WHERE created_date BETWEEN '${daterange.fromDate}' AND '${daterange.toDate}' AND (status = 'BOOKED' OR status = 'COMPLETED') AND telecaller_id = '${selectedUserId}'`,
      tAtt: `SELECT COUNT(ROWID) FROM Bookings WHERE created_date BETWEEN '${daterange.fromDate}' AND '${daterange.toDate}' AND telecaller_id = '${selectedUserId}'`
    };

    try {

      const results = await Promise.all(Object.values(queries).map(q => fetchzcql(q)));


      // 2. Extract Values (Adjusting for Zoho/CQL response structure)
      const booked = Number(results[0]?.data?.response?.[0]?.bookings?.['COUNT(ROWID)']) || 0;
      const revenue = Number(results[1]?.data?.response?.[0]?.bookings?.['SUM(total_amount)']) || 0;
      const attempts = Number(results[2]?.data?.response?.[0]?.bookings?.['COUNT(ROWID)']) || 0;
      const conversion = attempts === 0 ? 0 : ((booked / attempts) * 100).toFixed(1);

      // 3. Update the allusers state
      // This finds the specific user and merges the new metrics into their object
      setallusers(prevUsers => prevUsers.map(user => {
        if (user.ROWID === selectedUserId) {
          return {
            ...user,
            bookings: booked,
            revenue: revenue,
            conversion: `${Number(conversion || 0).toFixed(1)}`

          };
        }
        return user;
      }));
    } catch (error) {
      console.error("Failed to fetch user metrics:", error);
    }
  };


  const updateuser = async () => {
    var user = await getalluser_();
    var filteruser = user.users.filter((item) => {
      return item.user_management.status === "ACTIVE" && item.user_management.is_confirmed && item.role_name == 'App User'
    })
    setallusers(filteruser);
    setSelectedUserId(filteruser[0]['ROWID']);
  };


  useEffect(() => {
    fetchmetricsdata(globalTimeframe);
  }, [globalTimeframe])


  useEffect(() => {
    fetchusermetrics();
  }, [selectedUserId, teamFilter])

  useEffect(() => {
    updateuser();
  }, [])


  // Add/Keep this at the top of your component
  const activeUser = useMemo(() =>
    allusers?.find(u => u.ROWID === selectedUserId),
    [allusers, selectedUserId]
  );



  const activeChartKey = CHART_MAPPING[globalTimeframe] || "weekly";


  return (
    <div className="h-screen w-full bg-[#F8FAFC] overflow-hidden flex flex-col font-sans">

      {/* --- HEADER --- */}
      <header className="w-full bg-white border-b border-slate-100 px-6 py-4 flex items-center justify-between shrink-0 z-20 max-sm:pt-[5rem]">
        <div className="flex flex-col">
          <h1 className="text-xl font-black text-slate-900 tracking-tight leading-none">
            best surprise <span className="text-purple-500">Sales</span>
          </h1>
          <div className="flex items-center gap-2 mt-1.5">
            <div className="w-1.5 h-1.5 rounded-full bg-purple-500 animate-pulse" />
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Live Dashboard</p>
          </div>
        </div>

        <div className="flex items-center gap-4">
          <div className="hidden sm:flex items-center gap-2 pr-4 border-r border-slate-100">
            <Calendar size={16} className="text-slate-400" />
            <span className="text-xs font-bold text-slate-600">
              {new Date().toLocaleDateString("en-US", {
                month: "short",
                day: "numeric",
                year: "numeric"
              })}
            </span>

          </div>
          <div className="w-10 h-10 rounded-2xl bg-slate-900 flex items-center justify-center text-white shadow-lg border-2 border-white font-black">
            {currentLoggedInUser?.users?.name?.charAt(0)}
          </div>
        </div>
      </header>

      <main className="flex-1 w-full overflow-y-auto p-4 sm:p-6 lg:p-10 scroll-smooth">

        {/* --- GLOBAL MASTER FILTER --- */}
        <div className="mb-8 space-y-4">
          {/* Main Filter Bar */}
          <div className="flex flex-col md:flex-row md:items-center bg-white border border-slate-200 p-1.5 rounded-[20px] shadow-sm ring-1 ring-slate-100">
            {/* Label Section */}
            <div className="hidden md:flex items-center gap-2 px-4 py-2 border-r border-slate-100 mr-2">
              <Filter size={14} className="text-purple-500" />
              <span className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">Timeline</span>
            </div>

            {/* Tabs Container - Scrollbar Hidden */}
            <div className="flex flex-1 overflow-x-auto gap-1 p-0.5 scrollbar-hide"
              style={{ msOverflowStyle: 'none', scrollbarWidth: 'none', WebkitOverflowScrolling: 'touch' }}>
              <style>{`
                .scrollbar-hide::-webkit-scrollbar {
                  display: none;
                }
              `}</style>

              {["today", "yesterday", "this week", "this month", "custom"].map((type) => (
                <button
                  key={type}
                  onClick={() => setGlobalTimeframe(type)}
                  className={`flex-1 md:flex-none px-6 py-2.5 text-[11px] font-black uppercase tracking-wider rounded-xl transition-all duration-200 whitespace-nowrap text-center ${globalTimeframe === type
                    ? "bg-slate-900 text-white shadow-md shadow-slate-200 scale-[1.02]"
                    : "text-slate-500 hover:bg-slate-50 hover:text-slate-900"
                    }`}
                >
                  {type}
                </button>
              ))}
            </div>
          </div>

          {/* Custom Date Range Section */}
          {globalTimeframe === "custom" && (
            <div className="p-5 bg-white rounded-[24px] border border-slate-200 shadow-xl shadow-slate-100/50 animate-in fade-in slide-in-from-top-4 duration-500">
              <div className="flex flex-col gap-5">
                <div className="flex items-center gap-2 px-1">
                  <div className="w-1 h-4 bg-purple-500 rounded-full" />
                  <h4 className="text-[11px] font-black uppercase tracking-[0.15em] text-slate-500">
                    Custom Range
                  </h4>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div className="relative group">
                    <label className="absolute -top-2 left-3 bg-white px-1 text-[9px] font-black text-purple-600 z-10">START DATE</label>
                    <input
                      type="date"
                      value={customDateRange.start}
                      onChange={(e) => setcustomDateRange({ ...customDateRange, start: e.target.value })}
                      className="w-full bg-slate-50 border border-slate-100 rounded-xl px-4 py-3 text-xs font-bold text-slate-700 focus:ring-2 ring-purple-500/20 focus:bg-white focus:border-purple-500 outline-none transition-all"
                    />
                  </div>

                  <div className="relative group">
                    <label className="absolute -top-2 left-3 bg-white px-1 text-[9px] font-black text-purple-600 z-10">END DATE</label>
                    <input
                      type="date"
                      value={customDateRange.end}
                      onChange={(e) => setcustomDateRange({ ...customDateRange, end: e.target.value })}
                      className="w-full bg-slate-50 border border-slate-100 rounded-xl px-4 py-3 text-xs font-bold text-slate-700 focus:ring-2 ring-purple-500/20 focus:bg-white focus:border-purple-500 outline-none transition-all"
                    />
                  </div>

                  <button
                    onClick={() => fetchmetricsdata(globalTimeframe)}
                    className="flex items-center justify-center gap-2 bg-purple-500 hover:bg-purple-600 text-white px-6 py-3 rounded-xl text-xs font-black tracking-widest transition-all shadow-lg shadow-purple-200 active:scale-95"
                  >
                    APPLY <ArrowUpRight size={14} />
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* KPI CARDS */}
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 lg:gap-6 mb-8">
          <StatCard title="Total Bookings" value={metrics.total_booking} sub={`In ${globalTimeframe}`} icon={<Globe size={18} />} variant="purple" />
          <StatCard title="Conversion Rate" value={`${metrics.overall_conversion_rate}%`} sub="Avg Ratio" icon={<Zap size={18} />} variant="rose" />
          <StatCard title="Total Revenue" value={`₹${metrics.today_revenue}`} sub="Current Period" icon={<Briefcase size={18} />} variant="purple" />
        </div>

        {/* ANALYTICS SECTION */}
        <div className="grid grid-cols-12 gap-6 mb-10">
          <div className="col-span-12 xl:col-span-8 bg-white border border-slate-100 rounded-[32px] p-6 lg:p-8 shadow-sm">
            <div className="mb-8">
              <h3 className="text-lg font-bold text-slate-800">Booking Activity</h3>
              <p className="text-xs text-slate-400 font-medium capitalize">Visualizing {globalTimeframe} trends</p>
            </div>
            <div className="h-[300px] lg:h-[350px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData[activeChartKey]} margin={{ left: -20 }}>
                  <defs>
                    <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#10B981" stopOpacity={0.15} />
                      <stop offset="95%" stopColor="#10B981" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#F1F5F9" />
                  <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fill: '#94A3B8', fontSize: 10 }} dy={10} />
                  <YAxis axisLine={false} tickLine={false} tick={{ fill: '#94A3B8', fontSize: 10 }} width={40} />
                  <Tooltip content={<CustomTooltip unit="Bookings" color="#10B981" />} />
                  <Area type="monotone" dataKey="tasks" stroke="#10B981" strokeWidth={3} fill="url(#colorValue)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="col-span-12 xl:col-span-4 bg-white border border-slate-200 rounded-[32px] p-8 shadow-sm flex flex-col h-full min-h-[450px]">
            <h3 className="text-sm font-black text-slate-400 uppercase tracking-widest mb-6">Channel Distribution</h3>

            {(() => {
              const currentData = chartData[activeChartKey] || [];
              const totalTasks = currentData.reduce((sum, item) => sum + (Number(item.tasks) || 0), 0);
              const hasData = currentData.length > 0 && totalTasks > 0;

              if (!hasData) {
                return (
                  <div className="flex-1 flex flex-col items-center justify-center text-center space-y-3">
                    <div className="w-16 h-16 bg-slate-50 rounded-full flex items-center justify-center border border-dashed border-slate-200">
                      <Target size={24} className="text-slate-300" />
                    </div>
                    <div>
                      <p className="text-sm font-black text-slate-800">No Data Available</p>
                      <p className="text-[10px] font-bold text-slate-400 uppercase tracking-tight">Try adjusting your filters</p>
                    </div>
                  </div>
                );
              }

              return (
                <>
                  {/* Chart Section */}
                  <div className="h-[220px] w-full shrink-0 relative">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={currentData}
                          innerRadius="72%"
                          outerRadius="92%"
                          paddingAngle={6}
                          dataKey="tasks"
                          stroke="none"
                        >
                          {currentData.map((_, index) => (
                            <Cell
                              key={`cell-${index}`}
                              fill={COLORS[index % COLORS.length]}
                              className="hover:opacity-80 transition-opacity cursor-pointer"
                            />
                          ))}
                        </Pie>
                        <Tooltip content={<CustomTooltip unit="Bookings" />} />
                      </PieChart>
                    </ResponsiveContainer>
                    {/* Central Label for Crisp Look */}
                    <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                      <span className="text-2xl font-black text-slate-900 leading-none">{totalTasks}</span>
                      <span className="text-[9px] font-black text-slate-400 uppercase tracking-tighter">Total</span>
                    </div>
                  </div>

                  {/* Legend Section - No Scrollbar */}
                  <div
                    className="mt-8 space-y-2 overflow-y-auto pr-1 max-h-[180px]"
                    style={{ msOverflowStyle: 'none', scrollbarWidth: 'none' }}
                  >
                    <style>{`
            .no-scrollbar::-webkit-scrollbar { display: none; }
          `}</style>
                    <div className="no-scrollbar">
                      {currentData.map((entry, index) => (
                        <div
                          key={entry.name}
                          className="flex items-center justify-between p-2 rounded-xl hover:bg-slate-50 transition-colors group"
                        >
                          <div className="flex items-center gap-3">
                            <div
                              className="w-2 h-2 rounded-full shadow-sm"
                              style={{ backgroundColor: COLORS[index % COLORS.length] }}
                            />
                            <span className="text-xs font-bold text-slate-600 group-hover:text-slate-900 transition-colors">
                              {entry.name}
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-black text-slate-900">{entry.tasks}</span>
                            <span className="text-[9px] font-bold text-slate-300">
                              ({((entry.tasks / totalTasks) * 100).toFixed(0)}%)
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              );
            })()}
          </div>
        </div>

        <RevenueInsights></RevenueInsights>

        {/* --- SEPARATE USER PERFORMANCE SECTION --- */}
        <section className="mt-10 mb-10">
          <div className="flex flex-col xl:flex-row xl:items-center justify-between gap-6 mb-8">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-purple-100 text-purple-600 rounded-2xl shadow-sm">
                <Users size={22} />
              </div>
              <div>
                <h2 className="text-xl font-black text-slate-800">Team Insights</h2>
                <p className="text-xs text-slate-400 font-bold uppercase tracking-wider">Individual Performance Analysis</p>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row items-center gap-3">
              {/* STATUS FILTER */}
              <div className="flex bg-white border border-slate-200 rounded-2xl p-1 shadow-sm w-full sm:w-auto">
                {["today", "yesterday", "this week", "this month"].map((status) => (
                  <button
                    key={status}
                    onClick={() => setTeamFilter(status)}
                    className={`px-4 py-1.5 rounded-xl text-[10px] font-black uppercase tracking-tight transition-all flex-1 sm:flex-none ${teamFilter === status ? "bg-purple-600 text-white" : "text-slate-400 hover:text-slate-600"
                      }`}
                  >
                    {status}
                  </button>
                ))}
              </div>

              {/* USER SELECT DROPDOWN */}
              <div className="relative min-w-[240px] w-full sm:w-auto">
                <div className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400">
                  <UserIcon size={16} />
                </div>
                <select
                  value={selectedUserId}
                  onChange={(e) => setSelectedUserId(e.target.value)}
                  className="w-full pl-11 pr-10 py-3 bg-white border border-slate-200 rounded-2xl text-sm font-bold text-slate-700 outline-none appearance-none focus:ring-4 focus:ring-purple-50 transition-all cursor-pointer shadow-sm"
                >
                  {allusers.length > 0 ? (
                    allusers.map(user => (
                      <option key={user.ROWID} value={user.ROWID}>{user.name}</option>
                    ))
                  ) : (
                    <option disabled>No users found</option>
                  )}
                </select>
                <div className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none">
                  <ChevronDown size={16} />
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white border border-slate-100 rounded-[40px] p-6 lg:p-10 shadow-sm animate-in fade-in slide-in-from-bottom-4 duration-500">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">

              {/* Profile Bio */}
              <div className="lg:col-span-4 flex flex-col items-center text-center lg:items-start lg:text-left border-b lg:border-b-0 lg:border-r border-slate-100 pb-10 lg:pb-0 lg:pr-10">
                <div className="w-24 h-24 rounded-[32px] bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center text-white text-3xl font-black shadow-xl shadow-purple-100 mb-6">
                  {activeUser?.name.charAt(0)}
                </div>
                <h3 className="text-2xl font-black text-slate-800">{activeUser?.name}</h3>
                <p className="text-purple-600 font-bold text-sm uppercase tracking-widest mt-1">{activeUser?.role_name}</p>

                <div className="mt-6 flex items-center gap-2 px-4 py-2 bg-slate-50 rounded-xl border border-slate-100">
                  <div className={`w-2 h-2 rounded-full ${activeUser?.is_active ? 'bg-purple-500' : 'bg-slate-300'}`} />
                  <span className="text-xs font-black text-slate-500 uppercase">{activeUser?.user_management?.status}</span>
                </div>

                {/* Motivational Quote or Bio replacement */}
                <p className="mt-8 text-slate-400 text-sm font-medium leading-relaxed italic">
                  "Consistently meeting targets and driving high-value enterprise conversions through strategic outreach."
                </p>
              </div>

              {/* Detailed Metrics */}
              <div className="lg:col-span-8">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-10">
                  <DetailStat icon={<Briefcase size={18} />} label="Total Bookings" value={activeUser?.bookings} color="text-purple-600" />
                  <DetailStat icon={<TrendingUp size={18} />} label="Net Revenue" value={`₹${activeUser?.revenue}`} color="text-blue-600" />
                  <DetailStat icon={<Target size={18} />} label="Conversion" value={`${activeUser?.conversion}%`} color="text-purple-600" />
                </div>

                <div className="bg-slate-50/50 rounded-[32px] p-6 lg:p-8 border border-slate-100">
                  <div className="flex items-center justify-between mb-6">
                    <h4 className="font-black text-slate-700 text-sm uppercase tracking-wider">Conversion Efficiency</h4>
                    {/* <span className="text-xs font-bold text-purple-600 bg-purple-50 px-3 py-1 rounded-full border border-purple-100">Target: 75%</span> */}
                  </div>

                  <div className="relative pt-2">
                    <div className="flex mb-2 items-center justify-between">
                      <span className="text-xs font-black py-1 px-2 uppercase rounded-full text-purple-600 bg-purple-100">Current Output</span>
                      <span className="text-sm font-black text-purple-600">{activeUser?.conversion}%</span>
                    </div>
                    <div className="overflow-hidden h-4 mb-4 text-xs flex rounded-full bg-slate-200">
                      <div style={{ width: `${activeUser?.conversion}%` }} className="shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-gradient-to-r from-purple-500 to-purple-500 transition-all duration-1000"></div>
                    </div>
                    <div className="flex justify-between text-[10px] font-bold text-slate-400 uppercase">
                      <span>Poor</span>
                      <span>Average</span>
                      <span>Target</span>
                      <span>Excellent</span>
                    </div>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </section>

      </main>
    </div>
  );
}

// --- SUB COMPONENTS ---

function DetailStat({ icon, label, value, color }) {
  return (
    <div className="flex flex-col gap-3">
      <div className={`w-10 h-10 rounded-xl bg-white border border-slate-100 flex items-center justify-center shadow-sm ${color}`}>
        {icon}
      </div>
      <div>
        <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-0.5">{label}</p>
        <p className="text-xl font-black text-slate-800">{value}</p>
      </div>
    </div>
  );
}

function StatCard({ title, value, sub, icon, variant = "purple" }) {
  // Precise hex codes extracted from your image
  const themes = {
    purple: {
      cardBg: "#FFF9EE",
      iconBg: "#F3912E",
      accentText: "text-[#F3912E]"
    },
    rose: {
      cardBg: "#FFF1F9",
      iconBg: "#F75BB1",
      accentText: "text-[#F75BB1]"
    },
    purple: {
      cardBg: "#F5F3FF",
      iconBg: "#635BFF",
      accentText: "text-[#635BFF]"
    },
    purple: {
      cardBg: "#F0FDF4",
      iconBg: "#4ADE80",
      accentText: "text-[#4ADE80]"
    },
  };

  const theme = themes[variant] || themes.purple;

  return (
    <div
      style={{ backgroundColor: theme.cardBg }}
      className="rounded-[20px] p-6 shadow-sm border border-white/50 flex flex-col h-44 justify-between transition-transform hover:scale-[1.02]"
    >
      {/* Top Section: Title and Circular Icon */}
      <div className="flex justify-between items-start">
        <h3 className="text-sm font-semibold text-slate-700 tracking-tight">
          {title}
        </h3>
        <div
          style={{ backgroundColor: theme.iconBg }}
          className="w-10 h-10 rounded-full flex items-center justify-center text-white shadow-lg shadow-black/5"
        >
          {React.cloneElement(icon, { size: 18, strokeWidth: 2.5 })}
        </div>
      </div>

      {/* Middle Section: Value */}
      <div className="mt-2">
        <span className="text-3xl font-bold text-slate-900 tracking-tight">
          {value}
        </span>
      </div>

      {/* Bottom Section: Trend Indicator */}
      <div className="mt-4 flex items-center gap-2">
        <span className="text-xs font-medium text-slate-400">
          {sub}
        </span>
      </div>
    </div>
  );
}

const CustomTooltip = ({ active, payload, unit, color }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-slate-900 text-white p-4 rounded-2xl shadow-2xl border-none">
        <p className="text-[10px] font-black uppercase tracking-widest opacity-50 mb-1">{payload[0].payload.name}</p>
        <p className="text-lg font-bold" style={{ color }}>{payload[0].value} {unit}</p>
      </div>
    );
  }
  return null;
};

// function LegendRow({ label, value, dotColor }) {
//   return (
//     <div className="flex items-center justify-between">
//       <div className="flex items-center gap-2">
//         <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: dotColor }} />
//         <span className="text-sm font-bold text-slate-600">{label}</span>
//       </div>
//       <span className="text-sm font-black text-slate-900">{value}</span>
//     </div>
//   );
// }